import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Clock, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AnalysisDisplayProps {
  id: string;
  content: string;
  status: 'processing' | 'completed' | 'error';
}

/** Try to parse content as JSON with {simple, pro} fields. */
function parseAnalysisContent(raw: string): { simple: string; pro: string } | null {
  if (!raw || !raw.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.simple && parsed.pro) return parsed;
  } catch {
    // Not JSON — legacy plain-text result
  }
  return null;
}

const POLLING_INTERVAL = 3000; // 3 seconds
const MAX_RETRIES = 3;

const AnalysisDisplay: React.FC<AnalysisDisplayProps> = ({ id, content, status }) => {
  const [localContent, setLocalContent] = useState(content);
  const [localStatus, setLocalStatus] = useState(status);
  const [mode, setMode] = useState<'simple' | 'pro'>('simple');
  const mountedRef = useRef(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  // Memoized function to update local state
  const updateLocalState = useCallback((newContent: string, newStatus: string) => {
    if (mountedRef.current) {
      setLocalContent(newContent);
      setLocalStatus(newStatus as 'processing' | 'completed' | 'error');

      // Clear polling if status is no longer processing
      if (newStatus !== 'processing' && pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, []);

  // Memoized function to fetch analysis data with retries
  const fetchAnalysisData = useCallback(async () => {
    try {
      if (retryCountRef.current >= MAX_RETRIES) {
        console.log('Max retries reached, stopping polling');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        updateLocalState('Error al obtener el análisis después de varios intentos', 'error');
        return;
      }

      const { data, error } = await supabase
        .from('analyses')
        .select('status, result')
        .eq('id', id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching analysis:', error);
        retryCountRef.current++;
        return;
      }

      if (data) {
        retryCountRef.current = 0; // Reset retry count on successful fetch
        updateLocalState(data.result, data.status);
      }
    } catch (err) {
      console.error('Error fetching analysis data:', err);
      retryCountRef.current++;
    }
  }, [id, updateLocalState]);

  // Memoized function to setup realtime subscription
  const setupRealtimeSubscription = useCallback(async () => {
    try {
      // Initial fetch
      await fetchAnalysisData();

      // Setup realtime subscription
      const channel = supabase
        .channel(`analysis_${id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'analyses',
            filter: `id=eq.${id}`
          },
          (payload: any) => {
            if (mountedRef.current && payload.new) {
              console.log('Received update for analysis:', id);
              updateLocalState(payload.new.result, payload.new.status);
            }
          }
        )
        .subscribe((status) => {
          if (status.error) {
            console.error('Error subscribing to analysis updates:', status.error);
          } else {
            console.log('Successfully subscribed to analysis updates:', id);
          }
        });

      channelRef.current = channel;

      // Setup polling for processing status
      if (status === 'processing') {
        pollingIntervalRef.current = setInterval(fetchAnalysisData, POLLING_INTERVAL);
      }
    } catch (err) {
      console.error('Error setting up realtime subscription:', err);
      updateLocalState('Error al configurar las actualizaciones en tiempo real', 'error');
    }
  }, [id, status, fetchAnalysisData, updateLocalState]);

  useEffect(() => {
    // Update local state when props change
    updateLocalState(content, status);
  }, [content, status, updateLocalState]);

  useEffect(() => {
    setupRealtimeSubscription();

    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [setupRealtimeSubscription]);

  // Parse content — may be JSON {simple, pro} or legacy plain text
  const parsed = useMemo(() => parseAnalysisContent(localContent), [localContent]);
  const displayText = parsed
    ? (mode === 'simple' ? parsed.simple : parsed.pro)
    : localContent;

  if (localStatus === 'processing') {
    return (
      <div className="bg-dark-overlay/50 rounded-lg p-4">
        <div className="flex items-center justify-center text-yellow-500">
          <Clock className="h-5 w-5 mr-2 animate-spin" />
          <p>{localContent || 'Analizando el gráfico...'}</p>
        </div>
      </div>
    );
  }

  if (localStatus === 'error') {
    return (
      <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4">
        <div className="flex items-center text-red-500">
          <AlertCircle className="h-5 w-5 mr-2" />
          <p>{localContent || 'Error al analizar el gráfico'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-container">
      {/* Toggle Simple / Pro — only if both texts are available */}
      {parsed && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => setMode('simple')}
            className={`py-3 rounded-lg text-sm font-semibold transition-colors ${
              mode === 'simple'
                ? 'bg-primary text-white'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            Simple
          </button>
          <button
            onClick={() => setMode('pro')}
            className={`py-3 rounded-lg text-sm font-semibold transition-colors ${
              mode === 'pro'
                ? 'bg-primary text-white'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            Pro
          </button>
        </div>
      )}
      <ReactMarkdown
        className="prose prose-invert max-w-none"
        remarkPlugins={[remarkGfm]}
      >
        {displayText}
      </ReactMarkdown>
    </div>
  );
};

export default AnalysisDisplay;
